import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';
import BlogCTA from '../../components/BlogCTA';
import RelatedArticles from '../../components/RelatedArticles';

const schema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Article',
      'headline': 'How to Invoice as a Freelancer in Nigeria — FIRS, USD & Getting Paid',
      'description': 'Complete Nigerian freelancer invoicing guide: TIN registration with FIRS, USD billing, VAT on export services, and the best ways to receive international payments.',
      'url': 'https://www.invilic.com/blog/how-to-invoice-as-freelancer-nigeria',
      'datePublished': '2026-06-07',
      'dateModified': '2026-06-07',
      'author': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
      'publisher': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
    },
    {
      '@type': 'FAQPage',
      'mainEntity': [
        {
          '@type': 'Question',
          'name': 'Do Nigerian freelancers need to register for VAT?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'VAT registration in Nigeria is required for businesses with annual taxable turnover exceeding ₦25 million. Below this threshold, you are not required to charge VAT. When billing international clients for services, the export of services is zero-rated — no Nigerian VAT applies regardless of your registration status. Register your TIN with FIRS first, then VAT registration if your turnover reaches the threshold.' },
        },
        {
          '@type': 'Question',
          'name': 'What currency should Nigerian freelancers invoice in?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Most Nigerian freelancers invoice international clients in USD. This protects against naira depreciation and makes payment straightforward for overseas clients. For local Nigerian clients, invoice in NGN. Some platforms and clients also use GBP or EUR. Always agree the currency before starting work and state it explicitly on every invoice.' },
        },
        {
          '@type': 'Question',
          'name': 'How do Nigerian freelancers receive international payments?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'The most popular methods are: Payoneer (virtual USD account, withdrawable to GTBank, Access Bank, Zenith Bank, etc.), Grey (US, UK, and EU bank accounts for Nigerians), Wise (may have restrictions), Flutterwave, direct SWIFT wire transfer to a domiciliary account, and crypto (USDT/USDC on Binance P2P). PayPal withdrawals to Nigerian accounts remain very restricted.' },
        },
        {
          '@type': 'Question',
          'name': 'What should a Nigerian freelancer put on an invoice?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'A Nigerian freelance invoice should include: your full name and contact details, your TIN if available, the client name and address, a unique invoice number and date, a clear description of the services, the amount in the agreed currency (USD or NGN), your payment details (Payoneer, Grey, or bank account), and your payment terms. For international clients, add "VAT: Zero-rated — export of services".' },
        },
        {
          '@type': 'Question',
          'name': 'Does a Nigerian freelancer need a TIN to invoice international clients?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'A TIN (Tax Identification Number) from FIRS is not strictly required on invoices to international clients, but is recommended as it shows tax compliance. You can get a TIN free from the FIRS Joint Tax Board portal at jtb.gov.ng or at your nearest FIRS office. For invoices to Nigerian companies, a TIN is increasingly required for payments above certain amounts.' },
        },
      ],
    },
  ],
};

const faqs = schema['@graph'][1].mainEntity;

const invoiceFields = [
  { field: 'Your full name or business name', note: 'Use the name your Payoneer or bank account is registered under' },
  { field: 'Your contact details', note: 'Email address and optionally phone number' },
  { field: 'TIN (Tax Identification Number)', note: 'Recommended — get yours free from FIRS/JTB at jtb.gov.ng' },
  { field: "Client's name and address", note: 'Full name and billing address' },
  { field: 'A unique invoice number', note: 'e.g. INV-2026-001 — increment for every invoice' },
  { field: 'Invoice date and due date', note: 'Most international clients expect Net 14 or Net 30' },
  { field: 'Service description', note: 'Be specific — "Website development, 40 hours @ $35/hr" not just "services rendered"' },
  { field: 'Amount in USD (or agreed currency)', note: 'State currency explicitly — "USD 1,400.00" not just "1,400"' },
  { field: 'Payment details', note: 'Payoneer email, Grey account details, or bank SWIFT/account number' },
  { field: 'Zero-rated VAT note', note: 'Add "VAT: Zero-rated — export of services" when billing international clients' },
];

export default function HowToInvoiceFreelancerNigeriaPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="How to Invoice as a Freelancer in Nigeria — FIRS, USD & Getting Paid | Invilic Blog"
        description="Nigerian freelancer invoicing guide: FIRS TIN registration, USD billing, VAT on exports, and the best ways to receive international payments in Nigeria."
        canonical="https://www.invilic.com/blog/how-to-invoice-as-freelancer-nigeria"
        ogType="article"
        schema={schema}
      />
      <SeoNav />

      <main className="max-w-3xl mx-auto px-6 py-16">
        <header className="mb-10">
          <div className="flex items-center gap-2 mb-5 text-sm">
            <Link to="/blog" className="text-blue-600 dark:text-blue-400 hover:underline">Blog</Link>
            <ArrowRight className="w-3 h-3 text-gray-400" />
            <span className="text-gray-500 dark:text-gray-400">International</span>
          </div>
          <span className="inline-block bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs font-semibold px-2.5 py-1 rounded-full mb-4">International</span>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white leading-tight mb-4">
            How to Invoice as a Freelancer in Nigeria — FIRS, USD & Getting Paid
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm">June 7, 2026 · 6 min read</p>
        </header>

        <article className="text-gray-700 dark:text-gray-300 leading-relaxed">

          <p className="text-lg mb-6">
            Nigerian freelancers — developers, designers, writers, and virtual assistants — bill clients across the US, UK, Canada, and Europe every day. The process is simpler than most assume: invoice in USD, skip local VAT on export services, and use a reliable payment platform to receive your money.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">What to Include on a Nigerian Freelance Invoice</h2>
          <p className="mb-4">Whether billing local or international clients, include these fields on every invoice:</p>
          <div className="space-y-3 mb-8">
            {invoiceFields.map(({ field, note }, i) => (
              <div key={field} className="flex items-start gap-3">
                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 flex items-center justify-center text-xs font-bold mt-0.5">{i + 1}</div>
                <div className="text-sm">
                  <span className="font-medium text-gray-900 dark:text-white">{field}</span>
                  {note && <p className="text-gray-400 dark:text-gray-500 mt-0.5">{note}</p>}
                </div>
              </div>
            ))}
          </div>

          <BlogCTA
            text="Create a professional freelance invoice in USD, NGN, or any currency. PDF download free, no account needed."
            to="/invoice-generator-nigeria"
            btnText="Create Nigerian Invoice Free"
          />

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">VAT for Nigerian Freelancers — The Rules</h2>
          <p className="mb-4">Nigeria's standard VAT rate is 7.5%. Most freelancers are unaffected by it:</p>
          <div className="space-y-3 mb-6">
            {[
              { heading: 'Threshold: ₦25 million/year', body: 'VAT registration is mandatory only if your annual turnover exceeds ₦25 million. Most independent freelancers fall well below this threshold.' },
              { heading: 'Exports of services: zero-rated', body: 'Services provided to clients outside Nigeria are zero-rated for VAT. You do not charge 7.5% on invoices to US, UK, or EU clients — regardless of your VAT registration status.' },
              { heading: 'Local Nigerian clients', body: 'If your client is a Nigerian business and your turnover exceeds ₦25 million, charge 7.5% VAT and remit to FIRS monthly. Below the threshold, no VAT is required on local invoices either.' },
            ].map(({ heading, body }) => (
              <div key={heading} className="p-4 border border-gray-100 dark:border-gray-800 rounded-xl text-sm">
                <p className="font-semibold text-gray-900 dark:text-white mb-1">{heading}</p>
                <p className="text-gray-500 dark:text-gray-400">{body}</p>
              </div>
            ))}
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Getting Your TIN from FIRS</h2>
          <p className="mb-4">
            A Tax Identification Number (TIN) from the Federal Inland Revenue Service (FIRS) is your Nigerian tax ID. It is free and increasingly required for receiving payments from Nigerian companies.
          </p>
          <ul className="space-y-2 mb-8 text-sm">
            {[
              'Apply online at jtb.gov.ng (Joint Tax Board portal) — upload a valid ID and fill the form',
              'Or visit your nearest FIRS office in person — same day processing is usually available',
              'TIN is issued completely free — any agent charging for this is unofficial',
              'Your TIN is linked to your BVN (Bank Verification Number)',
              'Add your TIN to invoices for Nigerian clients as a sign of tax compliance',
            ].map(item => (
              <li key={item} className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">How to Receive International Payments in Nigeria</h2>
          <p className="mb-4">This is the most important practical question for Nigerian freelancers. Here are the main options:</p>
          <div className="space-y-3 mb-8">
            {[
              { method: 'Payoneer', detail: 'The most widely used platform for Nigerian freelancers. Get a virtual US bank account (ACH) and receive USD from international clients. Withdraw to any Nigerian bank within 1–2 business days. Accepted by Upwork, Fiverr, and direct clients.' },
              { method: 'Grey (grey.co)', detail: 'Nigerian-built platform offering US, UK, EU, and Canadian bank account details. Competitive rates and fast local transfers. Popular for freelancers billing UK and EU clients directly.' },
              { method: 'SWIFT wire transfer', detail: 'Receive USD directly to a domiciliary account at GTBank, Access Bank, or Zenith Bank. Best for large one-off payments from corporate clients. Sender fees are typically $15–50.' },
              { method: 'Flutterwave', detail: 'Popular for Nigerian businesses receiving international card payments and bank transfers from 30+ countries.' },
              { method: 'Crypto (USDT / USDC)', detail: 'Widely used by tech freelancers. P2P trading on Binance or direct wallet transfers convert to naira instantly. No restriction on amounts for compliant P2P platforms.' },
            ].map(({ method, detail }) => (
              <div key={method} className="border border-gray-100 dark:border-gray-800 rounded-xl p-4 text-sm">
                <p className="font-bold text-gray-900 dark:text-white mb-1">{method}</p>
                <p className="text-gray-500 dark:text-gray-400">{detail}</p>
              </div>
            ))}
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Which Currency to Invoice In</h2>
          <p className="mb-4">Invoice in USD by default — it is the global freelance standard and protects you from naira depreciation. Here is how to match currency to client:</p>
          <ul className="space-y-2 mb-8 text-sm">
            {[
              'US, Canada, Middle East clients → invoice in USD',
              'UK clients → invoice in GBP or USD (confirm with client)',
              'European clients → invoice in EUR',
              'Nigerian clients → invoice in NGN (or USD for large corporate clients)',
              'Always state the currency explicitly — write "USD 2,000.00" not just "2,000"',
            ].map(item => (
              <li key={item} className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none text-sm">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-5 pb-4 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{acceptedAnswer.text}</p>
              </details>
            ))}
          </div>

          <BlogCTA
            text="Create a professional invoice in USD, NGN, or any currency. Payoneer and SWIFT fields included. Free, no account needed."
            to="/invoice-generator-nigeria"
            btnText="Create Nigerian Invoice Free"
          />

        </article>
        <RelatedArticles current="/blog/how-to-invoice-as-freelancer-nigeria" />
      </main>
    </div>
  );
}
